import {
  getZoneAgentPermissions,
  ZONE_ANALYSIS_SECTION_ID,
} from 'features/libra/stepComponents/zoneAnalysis/aiSuggestedZones'
import { getCofModifier } from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import { useZoneAnalysisData } from 'features/libra/stepComponents/zoneAnalysis/ZoneAnalysisDataProvider'
import {
  getZoneTabAgentPermissions,
  ZONE_CARD_TABS,
  ZoneTabAgentPermissions,
} from 'features/libra/stepComponents/zoneAnalysis/zoneTabAutofill'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { FC, useMemo } from 'react'
import { RolodexPermissionsValues } from 'types'

import { ZonesEditorSurface } from './ZonesEditorSurface'

interface DrawingBasedZoneEditorProps {
  unitId: string
}

/** Adapts standalone NII zone state and agent settings to the shared surface. */
export const DrawingBasedZoneEditor: FC<DrawingBasedZoneEditorProps> = ({
  unitId,
}) => {
  const {
    zones,
    addZone,
    changeZone,
    removeZone,
    confirmProposal,
    replaceImageProposals,
    config,
    cof,
    confidence,
    isLoading: isZoneLoading,
    saveZone,
    isZoneDirty,
    isSaving,
    savingZoneId,
    saveError,
    drawingResetKey,
  } = useZoneAnalysisData()
  const { readOnly, isLoading: isTicketLoading } = useTicketAccess()
  const isEditorLoading = (isTicketLoading || isZoneLoading) && !isSaving
  // Extra AI-gating permissions the workflow author scoped to this section,
  // applied on top of the base Gecko-internal gate — same paradigm as every
  // other agent step (AiAutofillBanner), instead of hardcoding zones' own set.
  const zoneAgentPermissions = useMemo(
    () => getZoneAgentPermissions(config) as RolodexPermissionsValues[],
    [config]
  )
  const zoneTabAgentPermissions = useMemo<ZoneTabAgentPermissions>(
    () =>
      Object.fromEntries(
        ZONE_CARD_TABS.map((tab) => [
          tab,
          getZoneTabAgentPermissions(config, tab),
        ])
      ) as ZoneTabAgentPermissions,
    [config]
  )
  const prefillDerivationContext = useMemo(
    () => ({ cofModifier: getCofModifier(cof.level), confidence }),
    [cof.level, confidence]
  )

  return (
    <ZonesEditorSurface
      zones={zones}
      onAddZone={addZone}
      onChangeZone={changeZone}
      onRemoveZone={removeZone}
      onConfirmProposal={confirmProposal}
      onReplaceImageProposals={replaceImageProposals}
      unitId={unitId}
      agentSectionId={ZONE_ANALYSIS_SECTION_ID}
      zoneAgentPermissions={zoneAgentPermissions}
      visibleTabs={config.visibleTabs}
      zoneTabAgentPermissions={zoneTabAgentPermissions}
      prefillDerivationContext={prefillDerivationContext}
      onSaveZone={saveZone}
      isZoneDirty={isZoneDirty}
      isSaving={isSaving}
      savingZoneId={savingZoneId}
      getZoneSaveError={(zoneId) =>
        saveError?.zoneId === zoneId ? saveError.message : undefined
      }
      showCoverageBreakdown
      readonly={readOnly || isEditorLoading}
      isLoading={isEditorLoading}
      drawingResetKey={drawingResetKey}
    />
  )
}
